<template>
    <card-tabs selected-name="appointment">
        <template #labels>
            <appointment-card-tab-label :appointment-collection="appointmentCollection as ReactiveCollection<Appointment>" />

            <task-card-tab-label :task-collection="taskCollection as ReactiveCollection<Task>" />

            <card-tab-label name="transaction">
                Financieel
            </card-tab-label>

            <card-tab-label name="project">
                Project
            </card-tab-label>
        </template>

        <template #panels>
            <appointments-tab-panel
                :lead="lead"
                :appointment-collection="appointmentCollection as ReactiveCollection<Appointment>"
                :editable="leadIsEditable"
            />

            <tasks-tab-panel
                :lead="lead"
                :task-collection="taskCollection as ReactiveCollection<Task>"
                :editable="leadIsEditable"
            />

            <financial-tab-panel
                name="transaction"
                :lead="lead"
                :editable="leadIsEditable"
                @changed="changeTransaction($event)"
            />

            <project-tab-panel
                v-if="hasProjects"
                :lead="lead"
                :editable="leadIsEditable"
                :projects-collection="projectsCollection"
            />
        </template>
    </card-tabs>
</template>

<script lang="ts" setup>
    import useClient, { type ReactiveCollection } from '@composables/useClient';
    import { Appointment, Lead, Project, Task, Transaction } from '@keyvue/one-api-client';
    import AppointmentsTabPanel from '@components/Tabs/Panels/AppointmentsTabPanel.vue';
    import { CardTabLabel, CardTabs } from '@keyvue/one-ui';
    import TasksTabPanel from '@components/Tabs/Panels/TasksTabPanel.vue';
    import FinancialTabPanel from '@views/Lead/Detail/Tabs/FinancialTabPanel.vue';
    import TaskCardTabLabel from '@components/Tabs/Labels/TaskCardTabLabel.vue';
    import AppointmentCardTabLabel from '@components/Tabs/Labels/AppointmentCardTabLabel.vue';
    import ProjectTabPanel from '@views/Lead/Detail/Tabs/ProjectTabPanel.vue';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';
    import { computed } from 'vue';
    import { Referral, ReferralAPI } from '@keyvue/one-api-client-legacy';

    interface Props {
        lead: Lead;
    }

    const props = defineProps<Props>();
    const { getReactiveCollection, currentCompany } = useClient();
    const { notifySaving, notifySaved } = useNotify();
    const eventBus = useEventBus();

    const [
        appointmentCollection,
        taskCollection,
        projectsCollection,
    ] = await Promise.all([
        await getReactiveCollection(Appointment, { lead: props.lead.id }),
        await getReactiveCollection(Task, { lead: props.lead.id }),
        await getReactiveCollection(Project, { 'order[name]': 'asc', company: currentCompany.value.id.toString() }),
    ]);

    const hasProjects = computed(() => projectsCollection.count.value > 0);
    const leadIsEditable = computed(() => props.lead.assignee.company.equals(currentCompany.value));

    const changeReferralField = async (callback: (referral: Referral) => Referral) => {
        const closeNotification = notifySaving(undefined, 0);
        const legacyReferral = await ReferralAPI.get(props.lead.id.value);
        await ReferralAPI.update(props.lead.id.value, callback(legacyReferral));
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const changeTransaction = async (transaction: Transaction) => {
        await changeReferralField((referral) => ({
            ...referral,
            orderValue: transaction.orderValue,
            leadCommission: transaction.commission,
            productionDate: transaction.productionDate,
            transportDate: transaction.transportDate,
        } as Referral));

        eventBus.dispatch('resource-manipulated', 'Lead');
    };

    eventBus.subscribe<string>('resource-manipulated', async (params) => {
        if (params === 'Task') {
            await taskCollection.load();
        }
    });

    eventBus.subscribe<string>('resource-manipulated', async (params) => {
        if (params === 'Appointment') {
            await appointmentCollection.load();
        }
    });
</script>
